import { API_BASE_URL, API_ENDPOINTS, STORAGE_KEYS } from '@/consts'
import type { AuthResponse, LoginCredentials, User } from '@/types'

class ApiClient {
  private baseUrl: string

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...((options.headers as Record<string, string>) || {}),
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const response = await fetch(url, {
      ...options,
      headers,
    })

    if (!response.ok) {
      if (response.status === 401) {
        // Token expired, try to refresh
        const refreshed = await this.refreshToken()
        if (refreshed) {
          return this.request(endpoint, options)
        }
        // Refresh failed, logout
        localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN)
        localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN)
        window.location.href = '/login'
      }

      const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
      throw new Error(error.detail || `HTTP ${response.status}`)
    }

    // Handle empty response
    if (response.status === 204) {
      return {} as T
    }

    return response.json()
  }

  private async refreshToken(): Promise<boolean> {
    const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)
    if (!refreshToken) return false

    try {
      const response = await fetch(`${this.baseUrl}${API_ENDPOINTS.REFRESH}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      })

      if (response.ok) {
        const data: AuthResponse = await response.json()
        localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, data.access_token)
        localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, data.refresh_token)
        return true
      }
    } catch {
      // Refresh failed
    }
    return false
  }

  // Auth endpoints
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const formData = new URLSearchParams()
    formData.append('username', credentials.username)
    formData.append('password', credentials.password)

    const response = await fetch(`${this.baseUrl}${API_ENDPOINTS.LOGIN}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: formData,
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Login failed' }))
      throw new Error(error.detail)
    }

    return response.json()
  }

  async register(data: {
    email: string
    username: string
    password: string
    full_name: string
  }): Promise<User> {
    return this.request<User>(API_ENDPOINTS.REGISTER, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async getMe(): Promise<User> {
    return this.request<User>(API_ENDPOINTS.ME)
  }

  async updateMe(data: Partial<User>): Promise<User> {
    return this.request<User>(API_ENDPOINTS.ME, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  // User endpoints
  async getUsers(params?: { page?: number; page_size?: number; search?: string }) {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString())
    if (params?.search) queryParams.append('search', params.search)

    const query = queryParams.toString()
    return this.request<{ items: User[]; total: number }>(
      `${API_ENDPOINTS.USERS}${query ? `?${query}` : ''}`
    )
  }

  // Chat streaming
  async *chatStream(message: string, history?: { role: string; content: string }[]) {
    const response = await fetch(`${this.baseUrl}${API_ENDPOINTS.AGENT_CHAT_STREAM}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) || ''}`,
      },
      body: JSON.stringify({ message, history, stream: true }),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Chat failed' }))
      throw new Error(error.detail)
    }

    const reader = response.body?.getReader()
    if (!reader) throw new Error('No response body')

    const decoder = new TextDecoder()

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      const chunk = decoder.decode(value, { stream: true })
      const lines = chunk.split('\n')

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6)
          if (data === '[DONE]') return
          try {
            const parsed = JSON.parse(data)
            yield parsed
          } catch {
            yield data
          }
        }
      }
    }
  }

  async chat(message: string, history?: { role: string; content: string }[]): Promise<string> {
    const response = await this.request<{ response: string }>(API_ENDPOINTS.AGENT_CHAT, {
      method: 'POST',
      body: JSON.stringify({ message, history }),
    })
    return response.response
  }

  // Attendance endpoints
  async checkIn(location?: string) {
    return this.request<{ message: string; time: string }>(API_ENDPOINTS.ATTENDANCE_CHECK_IN, {
      method: 'POST',
      body: JSON.stringify({ location }),
    })
  }

  async checkOut(location?: string) {
    return this.request<{ message: string; time: string }>(API_ENDPOINTS.ATTENDANCE_CHECK_OUT, {
      method: 'POST',
      body: JSON.stringify({ location }),
    })
  }

  async getAttendanceStats(startDate?: string, endDate?: string) {
    const params = new URLSearchParams()
    if (startDate) params.append('start_date', startDate)
    if (endDate) params.append('end_date', endDate)
    const query = params.toString()
    return this.request<{ stats: Record<string, number>; total_work_hours: number; period: { start: string; end: string } }>(
      `${API_ENDPOINTS.ATTENDANCE}/stats${query ? `?${query}` : ''}`
    )
  }

  async getAttendanceCalendar(year: number, month: number) {
    return this.request<{
      year: number
      month: number
      records: Array<{
        date: string
        check_in: string | null
        check_out: string | null
        status: string
        work_hours: number | null
      }>
    }>(`${API_ENDPOINTS.ATTENDANCE}/calendar?year=${year}&month=${month}`)
  }

  // Leave endpoints
  async getLeaveBalance() {
    return this.request<{
      annual: { total: number; used: number; remaining: number }
      sick: { total: number; used: number; remaining: number }
      personal: { total: number; used: number; remaining: number }
    }>(API_ENDPOINTS.LEAVE_BALANCE)
  }

  async getLeaveRequests(params?: { page?: number; page_size?: number; status?: string }) {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString())
    if (params?.status) queryParams.append('status', params.status)
    const query = queryParams.toString()
    return this.request<{ items: any[]; total: number; page: number; page_size: number }>(
      `${API_ENDPOINTS.LEAVE_REQUESTS}${query ? `?${query}` : ''}`
    )
  }

  async createLeaveRequest(data: {
    leave_type: string
    start_date: string
    end_date: string
    days: number
    reason: string
  }) {
    return this.request<{ id: number; message: string }>(API_ENDPOINTS.LEAVE_REQUESTS, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async cancelLeaveRequest(requestId: number) {
    return this.request<{ message: string }>(`${API_ENDPOINTS.LEAVE_REQUESTS}/${requestId}/cancel`, {
      method: 'POST',
    })
  }

  // Performance endpoints
  async getPerformanceReviews(params?: { page?: number; page_size?: number; year?: number }) {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString())
    if (params?.year) queryParams.append('year', params.year.toString())
    const query = queryParams.toString()
    return this.request<{ items: any[]; total: number; page: number; page_size: number }>(
      `${API_ENDPOINTS.PERFORMANCE_REVIEWS}${query ? `?${query}` : ''}`
    )
  }

  async getPerformanceStats(year?: number) {
    const params = new URLSearchParams()
    if (year) params.append('year', year.toString())
    const query = params.toString()
    return this.request<{
      year: number
      average_score: number | null
      reviews_count: number
      trend: number | null
      breakdown: {
        work_quality: number
        work_quantity: number
        attendance: number
        teamwork: number
        initiative: number
      }
    }>(`${API_ENDPOINTS.PERFORMANCE}/stats${query ? `?${query}` : ''}`)
  }

  // Documents endpoints
  async getFolders() {
    return this.request<{ items: any[] }>(API_ENDPOINTS.DOCUMENT_FOLDERS)
  }

  async createFolder(name: string, description?: string, parentId?: number) {
    return this.request<{ id: number; name: string; message: string }>(API_ENDPOINTS.DOCUMENT_FOLDERS, {
      method: 'POST',
      body: JSON.stringify({ name, description, parent_id: parentId }),
    })
  }

  async getDocuments(params?: { page?: number; page_size?: number; folder_id?: number; search?: string }) {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString())
    if (params?.folder_id) queryParams.append('folder_id', params.folder_id.toString())
    if (params?.search) queryParams.append('search', params.search)
    const query = queryParams.toString()
    return this.request<{ items: any[]; total: number; page: number; page_size: number }>(
      `${API_ENDPOINTS.DOCUMENTS}${query ? `?${query}` : ''}`
    )
  }

  async uploadDocument(file: File, title?: string, folderId?: number, isPublic: boolean = false) {
    const formData = new FormData()
    formData.append('file', file)
    if (title) formData.append('title', title)
    if (folderId) formData.append('folder_id', folderId.toString())
    formData.append('is_public', isPublic.toString())

    const response = await fetch(`${this.baseUrl}${API_ENDPOINTS.DOCUMENTS}/upload`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) || ''}`,
      },
      body: formData,
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload failed' }))
      throw new Error(error.detail)
    }

    return response.json()
  }

  async deleteDocument(documentId: number) {
    return this.request<{ message: string }>(`${API_ENDPOINTS.DOCUMENTS}/${documentId}`, {
      method: 'DELETE',
    })
  }

  // Employee endpoints
  async getEmployees(params?: {
    page?: number
    page_size?: number
    dept_code?: string | null
    level_code?: string | null
    status?: string | null
    manager_id?: string | null
    search?: string | null
  }): Promise<{ items: import('@/types').Employee[]; total: number; page: number; page_size: number }> {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString())
    if (params?.dept_code) queryParams.append('dept_code', params.dept_code)
    if (params?.level_code) queryParams.append('level_code', params.level_code)
    if (params?.status) queryParams.append('status', params.status)
    if (params?.manager_id) queryParams.append('manager_id', params.manager_id)
    if (params?.search) queryParams.append('search', params.search)
    const query = queryParams.toString()
    return this.request<{ items: import('@/types').Employee[]; total: number; page: number; page_size: number }>(
      `${API_ENDPOINTS.EMPLOYEES}${query ? `?${query}` : ''}`
    )
  }

  async getEmployee(employeeId: string): Promise<import('@/types').Employee> {
    return this.request<import('@/types').Employee>(`${API_ENDPOINTS.EMPLOYEES}/${employeeId}`)
  }

  async createEmployee(data: import('@/types').EmployeeCreate): Promise<import('@/types').Employee> {
    return this.request<import('@/types').Employee>(API_ENDPOINTS.EMPLOYEES, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateEmployee(employeeId: string, data: import('@/types').EmployeeUpdate): Promise<import('@/types').Employee> {
    return this.request<import('@/types').Employee>(`${API_ENDPOINTS.EMPLOYEES}/${employeeId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteEmployee(employeeId: string) {
    return this.request<{ message: string }>(`${API_ENDPOINTS.EMPLOYEES}/${employeeId}`, {
      method: 'DELETE',
    })
  }

  async getSubordinates(employeeId: string): Promise<import('@/types').Employee[]> {
    return this.request<import('@/types').Employee[]>(`${API_ENDPOINTS.EMPLOYEES}/${employeeId}/subordinates`)
  }

  async getLevels(): Promise<import('@/types').LevelDefinition[]> {
    return this.request<import('@/types').LevelDefinition[]>(API_ENDPOINTS.LEVELS)
  }

  async getDepartments(): Promise<import('@/types').Department[]> {
    return this.request<import('@/types').Department[]>(API_ENDPOINTS.DEPARTMENTS)
  }

  async getNextEmployeeId(): Promise<{ next_id: string }> {
    return this.request<{ next_id: string }>(`${API_ENDPOINTS.EMPLOYEES}/next-id`)
  }

  // Work History endpoints
  async getWorkHistory(employeeId: string): Promise<import('@/types').WorkHistory[]> {
    return this.request<import('@/types').WorkHistory[]>(`${API_ENDPOINTS.EMPLOYEES}/${employeeId}/work-history`)
  }

  async createWorkHistory(employeeId: string, data: import('@/types').WorkHistoryCreate): Promise<import('@/types').WorkHistory> {
    return this.request<import('@/types').WorkHistory>(`${API_ENDPOINTS.EMPLOYEES}/${employeeId}/work-history`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateWorkHistory(employeeId: string, historyId: number, data: import('@/types').WorkHistoryUpdate): Promise<import('@/types').WorkHistory> {
    return this.request<import('@/types').WorkHistory>(`${API_ENDPOINTS.EMPLOYEES}/${employeeId}/work-history/${historyId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteWorkHistory(employeeId: string, historyId: number) {
    return this.request<{ message: string }>(`${API_ENDPOINTS.EMPLOYEES}/${employeeId}/work-history/${historyId}`, {
      method: 'DELETE',
    })
  }
}

export const api = new ApiClient(API_BASE_URL)
